import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { App } from '@capacitor/app'
import { LocalNotifications } from '@capacitor/local-notifications'
import { IonToast } from '@ionic/react'
import { getQuickActionsSelector } from '../../features/quickActions/selectors/quickActions.selector'
import { ISelectedQuickActionButton, ITime } from '../../features/quickActions/store/quickActions.types'
import { checkLocalNotificationPermissions } from '../../utilities/localNotifications.util'
import { resetLocalNotificationsToCancel } from '../../features/quickActions/store/quickActions.actions'
import { isAndroid } from '../../utilities/isAndroid.util'
import { v4 as uuidv4 } from 'uuid'
import { ToastColor } from '../toast/store/toast.constants'
import { getToastProps } from '../toast/toastIcon.util'

type PopupDataType = {
  key: string
  parentQuickAction: string
  title2: string
  label: string
  showPopup: boolean
  count: number
}

interface PendingTimeout {
  buttonId: string
  timeout: NodeJS.Timeout
}

const NotificationsPopup = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [popupData, setPopupData] = useState<PopupDataType[]>([])
  const popupDataRef = useRef(popupData)

  const [pendingTimeouts, setPendingTimeouts] = useState<PendingTimeout[]>([])

  const quickActions = useSelector(getQuickActionsSelector)
  const { savedQuickActionButtonsForNotification, localNotifications, localNotificationsToCancel } = quickActions

  const queueInAppNotification = (button: ISelectedQuickActionButton) => {
    const flashTimes = button?.props?.flashTimes

    flashTimes?.forEach((flashTime) => {
      const hours = Number(flashTime.hours)
      const minutes = Number(flashTime.minutes)
      const seconds = Number(flashTime.seconds)

      const timeToNotification = 1000 * (hours * 3600 + minutes * 60 + seconds)
      if (!timeToNotification) return

      const notificationId = uuidv4()

      const newPendingTimeouts = pendingTimeouts.filter((pendingTimeout) => {
        if (pendingTimeout.buttonId === button?.props?.label) {
          clearTimeout(pendingTimeout.timeout)
          return false
        }
        return true
      })

      const timeout = setTimeout(() => {
        fireInAppNotification(button, notificationId)
      }, timeToNotification)

      const newPendingTimeout: PendingTimeout = {
        buttonId: button?.props?.label || '',
        timeout,
      }
      setPendingTimeouts([...newPendingTimeouts, newPendingTimeout])
    })
  }

  const getNotificationDeliveryTime = (flashTime: ITime): Date => {
    const secondsUntilNotification = 1000 * Number(flashTime.seconds)
    const minutesUntilNotification = 1000 * 60 * Number(flashTime.minutes)
    const hoursUntilNotification = 1000 * 60 * 60 * Number(flashTime.hours)
    const timeToNotification = secondsUntilNotification + minutesUntilNotification + hoursUntilNotification

    const deliveryTime = new Date(Date.now() + timeToNotification)
    return deliveryTime
  }

  const queueLocalNotification = async (button: ISelectedQuickActionButton) => {
    const permissionStatus = await checkLocalNotificationPermissions()
    if (permissionStatus.display !== 'granted') {
      return
    }

    if (button.props?.flashTimes && button.props.showFlash) {
      for (let i = 0; i < button.props.flashTimes.length; i++) {
        const flashTime = button.props.flashTimes[i]

        const deliveryTime = getNotificationDeliveryTime(flashTime)
        if (!deliveryTime) return

        const notificationMessage = `${button.props.parentQuickAction} ${button.props.label} ${button.props.title2} timer has expired`
        const notificationId =
          localNotifications.find((localNotification) => {
            return localNotification.buttonId === button?.props?.buttonId
          })?.id || 0

        LocalNotifications.schedule({
          notifications: [
            {
              title: t('notifications.appTitle'),
              body: notificationMessage,
              id: notificationId,
              actionTypeId: '',

              schedule: {
                at: deliveryTime,
              },
              // @ts-ignore
              silent: true,
              extra: null,
            },
          ],
        })
      }
    }
  }

  const queueNotificationPopup = () => {
    const latestButton = savedQuickActionButtonsForNotification[savedQuickActionButtonsForNotification.length - 1]

    queueInAppNotification(latestButton)
    if (latestButton) queueLocalNotification(latestButton)
  }

  const fireInAppNotification = (button: ISelectedQuickActionButton, id: string) => {
    setPopupData((oldPopupData) => [
      ...oldPopupData,
      {
        key: id,
        parentQuickAction: button?.props?.parentQuickAction || '',
        title2: button?.props?.title2 || '',
        label: button?.props?.label || '',
        showPopup: true,
        count: button?.props?.count || 0,
      },
    ])
  }

  useEffect(() => {
    if (savedQuickActionButtonsForNotification.length) {
      queueNotificationPopup()
    } else {
      setPopupData([])
      pendingTimeouts.forEach((pendingTimeout) => {
        clearTimeout(pendingTimeout.timeout)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedQuickActionButtonsForNotification])

  const cancelNotifications = async () => {
    localNotificationsToCancel.forEach((notification) => {
      LocalNotifications.cancel({
        notifications: [{ id: notification.id }],
      })
    })
    dispatch(resetLocalNotificationsToCancel())
  }

  useEffect(() => {
    if (!localNotificationsToCancel.length) return

    cancelNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localNotificationsToCancel])

  const suppressLocalNotificationsWhileInForeground = () => {
    if (!isAndroid()) return

    LocalNotifications.addListener('localNotificationReceived', async (notification) => {
      const { isActive } = await App.getState()

      if (isActive) {
        LocalNotifications.removeDeliveredNotifications({ notifications: [notification] })
      }
    })
  }

  useEffect(() => {
    suppressLocalNotificationsWhileInForeground()

    return () => {
      LocalNotifications.removeAllListeners()
    }
  }, [])

  useEffect(() => {
    popupDataRef.current = popupData
  }, [popupData])

  return (
    <>
      {popupData.map((data) => (
        <IonToast
          key={data.key + data.count.toString()}
          position='top'
          isOpen={data.showPopup}
          message={`${data.parentQuickAction} ${data.label} ${data.title2} timer has expired`}
          {...getToastProps(ToastColor.Warning)}
          onDidDismiss={() => {
            data.showPopup = false
          }}
        />
      ))}
    </>
  )
}

export default NotificationsPopup
